import {
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
  OnInit,
  OnDestroy,
  ViewChild,
  EventEmitter,
} from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs';

import * as _ from 'lodash';

import { getItemValidators, initValidators, hasServerError } from 'utils/utils';
import { BaseSegmentComponent } from '../../base/_base-segment.component';
import { TableEditableComponent } from 'shared/table/table-editable/table-editable.component';
import { HiddenScrollComponent } from 'shared/scroll/hidden-scroll/hidden-scroll.component';

import { ProductPanelService } from '../../../product-panel.service';
import { BrandService } from 'shared/brand/brand.service';
import { ItemDetailService } from 'shared/item-detail/item-detail.service';
import { createOption, defaultOption } from 'shared/form/select/option-helpers';
import { ScrollToService } from 'shared/base/scroll-to.service';
import { SearchService } from 'shared/search/search.service';

import { UserAttribute } from '../../../models/user-attribute.model';
import { pageOptions } from '../../../page-options';
import {takeWhile} from 'rxjs/operators';


@Component({
  moduleId: module.id,
  selector: 'user-attributes',
  templateUrl: 'user-attributes.component.html',
  styleUrls: ['user-attributes.component.scss'],
  providers: [ScrollToService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserAttributesComponent extends BaseSegmentComponent implements OnInit, OnDestroy {

  data: Array<any> = [];
  attributesFormGroup: UntypedFormGroup;
  choosingNewAttributeType: boolean = false;
  isActive: boolean = true;

  @Input() obsData: Observable<any>;
  @Input() unitsData: Array<any>;
  @Input() fields: Array<any>;
  @Input() declare headers: Array<any>;
  @Input() hasPartType: boolean;
  @Input() declare itemDetailValidation: any;

  @ViewChild(TableEditableComponent, {static: false}) tableEditable: TableEditableComponent | void;
  @ViewChild(HiddenScrollComponent, {static: true}) hiddenScroll: HiddenScrollComponent | void;

  @Output() removeRows = new EventEmitter();

  constructor(
    protected productPanelService: ProductPanelService,
    protected brandService: BrandService,
    protected itemDetailService: ItemDetailService,
    protected scrollToService: ScrollToService,
    protected searchService: SearchService,
    protected fb: UntypedFormBuilder,
    protected route: ActivatedRoute,
    protected ref: ChangeDetectorRef,
  ) {
    super(productPanelService, brandService, itemDetailService, searchService, route, ref);
  }

  // getters
  get attributes(): UntypedFormArray {
    return this.attributesFormGroup.get('attributes') as UntypedFormArray;
  }

  get isEmpty() {
    return !this.data.length;
  }

  // hooks
  ngOnInit() {
    this.attributesFormGroup = this.fb.group({
      attributes: this.fb.array([])
    });
    this.scrollToService.init({
      pageScrollOffset: pageOptions.headerMinHeight - pageOptions.segmentItemOffsets
    });
    this.obsData
    .pipe(takeWhile(() => this.isActive))
    .subscribe((data: any) => {

      // create formArray
      this.attributesFormGroup.setControl('attributes', this.fb.array([]));

      // handle each attribute
      this.data = data.map((item: any) => {
        return this.handleAttribute(item);
      });

      // add attribute to formArray
      _.forEach(this.data, attribute => {
        this.attributes.push(this.createAttributeFG(attribute));
      });
    });

    this.productPanelService.uiModel.isModalShown
    .pipe(takeWhile(() => this.isActive))
    .subscribe((isModalShown: boolean) => {
      if (isModalShown && this.hiddenScroll) {
        this.hiddenScroll.defineContentWidth();
      }
    });

  }

  ngOnDestroy() {
    this.isActive = false;
  }

  // public
  public createAttributeFG(attribute: any) {
    const attributeModel = this.createAttributeModel(attribute);
    return this.fb.group(attributeModel);
  }

  public save() {
    console.log('save attributes: ', this.attributesFormGroup.value);
  }

  public cancel() {
    console.log('cancel attributes');
  }

  public handleAttribute(attribute: any) {
    const handledAttribute = {...attribute};

    // set assignment_id for non-PAdb attributes
    if (!handledAttribute.assignment_id) {
      handledAttribute.assignment_id = null;
    }

    // convert attribute value to array
    if (!Array.isArray(handledAttribute.value)) {
      handledAttribute.value = [handledAttribute.value];
    }

    // if it's no valid_attribute_values field - add it
    if (handledAttribute.valid_attribute_values && handledAttribute.valid_attribute_values.length) {
      handledAttribute.valid_attribute_values = handledAttribute.valid_attribute_values.map((value: any) => {
        return createOption(value);
      });
      // add default option
      handledAttribute.valid_attribute_values.splice(0, 0, defaultOption);
    } else {
      handledAttribute.valid_attribute_values = null;
    }

    // if uom is select
    if (handledAttribute.meta_uom_codes) {
      // add default option
      handledAttribute.meta_uom_codes.splice(0, 0, defaultOption);
    }

    // if it's no style_id field - add it
    if (!handledAttribute.style_id) {
      handledAttribute.style_id = null;
    }
    // if style is select
    if (handledAttribute.styles && handledAttribute.styles.length) {
      // add default option
      handledAttribute.styles.splice(0, 0, defaultOption);
    }

    // is PAdb
    if (!handledAttribute.is_padb) {
      handledAttribute.is_padb = 'N';
    }

    return handledAttribute;
  }

  public createAttributeModel(attribute: any) {
    const model = new UserAttribute();

    _.each(this.fields, (field: any) => {
      if (!field.field_map) { return; }
      let validators = getItemValidators(field.validation, null);
      const asyncValidators = getItemValidators(field.asyncValidation, null);
      if (field.field_map === 'value') {
        // value is Array
        // if attrubute is padb & value has meta_data,
        // then bind min/max length from meta_data
        if (!attribute.valid_attribute_values && attribute.meta_data) {
          validators = this.getMetaDataValidators(attribute.meta_data);
        }
        const valueFormGroups = attribute.value.map((val: any) => {
          return new UntypedFormControl(val, validators, asyncValidators);
        });
        const valueFormArray = new UntypedFormArray(valueFormGroups);
        _.set(model, field.field_map, valueFormArray);
      } else {
        const value = _.get(attribute, field.item_name, '');
        _.set(model, field.field_map, new UntypedFormControl(value, validators, asyncValidators));
      }
    });
    return model;
  }

  public addAttributes(attributes?: any, showComplete?: boolean) {
    if (!attributes.length) {
      attributes = [{}];
    }

    const lastIndex = this.data.length;
    _.forEach(attributes, (attribute: any) => {
      attribute.showComplete = showComplete ? showComplete : false;

      if (!attribute.attribute_id) {
        attribute.attribute_id = attribute.id !== undefined ? attribute.id : attribute.name;
      }
      delete attribute.id;
      attribute.value = '';
      const newAttr = this.handleAttribute(attribute);
      const group = this.createAttributeFG(newAttr);
      this.data.push(newAttr);
      this.attributes.push(group);
    });

    _.defer(() => {
      this.scrollToService.scrollTo({scrollTarget: `.attribute-${lastIndex}`});
      _.delay(() => {
        if (this.tableEditable instanceof TableEditableComponent) {
          this.tableEditable.toggleLastCountCollapse(attributes.length);
        }
      }, 1000);
    });
  }

  valueErrorDetail(item: any, dataItem: any, formControl: UntypedFormControl, index: number, controlIndex: number) {
    if (!item || !dataItem || !formControl) { return ''; }
    if (dataItem.is_padb === 'Y') {
      if (formControl.valid) {
        // check if PAdb ID is not valid for the current part type (SPD2-3188)
        if (hasServerError(this.itemDetailValidation, this.getErrorPath(index, 'value'), undefined, controlIndex)) {
          return 'PAdb attribute is not valid for this Part type';
        } else {
          return '';
        }
      }
      // padb meta_data rules
      const maxLength = this.maxLength('value', dataItem);
      const decimalPoints = _.toNumber(_.get(dataItem, 'meta_data.rules.decimal_points', 0));
      return `Attribute value cannot be longer than ${maxLength} characters` +
        (decimalPoints ? ` including ${decimalPoints} decimal places` : '') +
        (this.isInputNumber(dataItem) ? ' and cannot have Alphabetical characters' : '');
    } else {
      if (formControl.valid) { return ''; }
      return _.get(item, 'validation.PIES_6_5.message', '');
    }
  }

  isInputNumber(item: any): boolean {
    if (item.is_padb === 'Y') {
      return _.includes(['number', 'decimal'], _.get(item, 'meta_data.data_type'));
    } else {
      return false;
    }
  }

  isInputDecimal(item: any): boolean {
    if (item.is_padb === 'Y') {
      return _.get(item, 'meta_data.data_type') === 'decimal';
    } else {
      return false;
    }
  }

  isAttributeInvalid(formGroup: UntypedFormGroup, data: any, index: number): boolean {
    if (data.is_padb === 'Y') {
      if (formGroup.get('value').valid && formGroup.get('name').valid) {
        // check if PAdb ID is not valid for the current part type (SPD2-3188)
        return hasServerError(this.itemDetailValidation, this.getErrorPath(index, 'value'), undefined, undefined, true);
      }
    }
    return formGroup.get('name').invalid || formGroup.get('value').invalid;
  }

  isValueInvalid(control: UntypedFormControl, data: any, index: number, controlIndex: number): boolean {
    if (data.is_padb === 'Y' && control.valid) {
      // check if PAdb ID is not valid for the current part type (SPD2-3188)
      return hasServerError(this.itemDetailValidation, this.getErrorPath(index, 'value'), undefined, controlIndex);
    }
    return control.invalid;
  }

  // handlers
  onCompleteClick(attribute: any, index: number) {
    _.defer(() => {
      this.data[index].showComplete = false;
      if (this.tableEditable instanceof TableEditableComponent) {
        this.tableEditable.toggleItemCollapse(index);
      }
    });
  }

  onAddNewAttributeClick() {
    this.choosingNewAttributeType = true;
  }

  onAddNewTypeAttributeClick(withUnits: boolean) {
    const newAttr: any = {
      name: '',
      value: [''],
    };
    if (withUnits) {
      newAttr.uom_description = '';
      newAttr.uom = '';
    }
    this.addAttributes([newAttr], true);
    this.choosingNewAttributeType = false;
  }

  onDeleteValueClick(valueControl: UntypedFormControl, formGroup: UntypedFormGroup) {
    const formArray: any = formGroup.controls.value;
    const index = formArray.controls.indexOf(valueControl);
    formArray.removeAt(index);
    formArray.markAsDirty();
  }

  onAddValueClick(valueControl: UntypedFormControl, formGroup: UntypedFormGroup) {
    const formArray: any = formGroup.controls.value;
    formArray.push(new UntypedFormControl(''));
    formArray.markAsDirty();
  }

  onSelectChange(selectedItem: any, control: any) {
    control.setValue(selectedItem.option);
    this.attributes.markAsDirty();
  }

  onUomSelectChange(selectedItem: any, formGroup: any, dataItem: any) {
    const uomTitle = this.getUomTitle(selectedItem.option, dataItem);
    if (uomTitle) {
      formGroup.get('uom_description').setValue(uomTitle);
    }
  }

  onRemoveRows(rows: any[]) {
    const attributesIds = _.map(rows, row => {
      return row.row.get('attribute_id').value;
    });
    _.forEach(attributesIds, id => {
      const index = _.findIndex(this.data, {attribute_id: id});
      this.data.splice(index, 1);
    });
    this.removeRows.emit(attributesIds);
  }

  // protected
  protected maxLength(name: string, dataItem?: any): number {
    if (name === 'value' && dataItem.is_padb === 'Y') {
      return _.toNumber(_.get(dataItem, 'meta_data.rules.max_length', 2000));
    } else {
      return this.getAttributeFieldByName(name).validation.PIES_6_5.maxLength;
    }
  }

  protected getAttributeFieldByName(name: string): any {
    return this.getFieldByName(name, this.fields);
  }

  // private
  private getCellSizeClass(attribute: any): string {
    const uom = attribute.uom;
    const style = attribute.style_id;

    const meta_uom_codes = attribute.meta_uom_codes;
    const styles = attribute.styles;
    return (uom && style) ||
    (meta_uom_codes && meta_uom_codes.length && styles && styles.length) ?
      'size-quartered' :
      'size-half';
  }

  private getValue(formGroup: UntypedFormGroup) {
    return formGroup.controls.value.value.join('<br>');
  }

  private isArray(obj: any) {
    return obj instanceof Array;
  }

  private getUnits(item: any) {
    return item.meta_uom_codes ? item.meta_uom_codes : this.unitsData;
  }

  private getStyleTitle(styleId: string, index: number) {
    const item: any = _.find(this.data[index].styles, {option: styleId});
    return item ? item.title : '';
  }

  private getUomTitle(uom: any, dataItem: any) {
    const units = this.getUnits(dataItem);
    const option: any = _.find(units, {option: uom});
    return option ? option.title : null;
  }

  private getMetaDataValidators(metaData: any): Array<any> {
    const regex = _.get(metaData, 'rules.regex', null);
    const decimalPoints = _.toNumber(_.get(metaData, 'rules.decimal_points', null));
    let decimalValidator: any;
    if (decimalPoints) {
      decimalValidator = Validators.pattern(new RegExp(`^\\d+\\.\\d{${decimalPoints}}$`));
    }
    return this.initValidators({
      'required': true,
      'minLength': _.toNumber(_.get(metaData, 'rules.min_length', 1)),
      'maxLength': _.toNumber(_.get(metaData, 'rules.max_length', 2000)),
      'pattern': regex ? new RegExp(regex, 'g') : null,
      'validator': decimalValidator,
    });
  }

  private getErrorPath(attributeIndex: number, name: string, controlIndex?: number): string {
    const controlPath = !_.isNil(controlIndex) ? `.${controlIndex}` : '';
    return `attributes.${attributeIndex}.${name}${controlPath}`;
  }
}
